import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  selectCanvases,
  fetchUserCanvases,
  createCanvas,
  updateCanvas,
  deleteCanvas,
  selectCanvasLoading,
  fetchCanvasesSharedByMe
} from '../../../../app/store/slices/canvasSlice';
import {
  fetchUserInvitations,
  fetchGroupData
} from '../../../../app/store/slices/groupSlice';
import { fetchUserById, fetchUserData, selectUser, selectUserLoading } from '../../../../app/store/slices/userSlice';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { shareCanvas } from '../../../../app/store/slices/canvasSlice';
import { useCanvas} from '../../../dashboard/context/CanvasContext';
import DataStatusMessage from '../../../shared/components/modals/DataStatusMessage';
import {fetchSubscriptionData, selectCurrentPlan } from '../../../../app/store/slices/subscriptionSlice';


const ManageCanvasContent = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('My Canvas');
  const { owned: ownedCanvases, shared: sharedCanvases } = useSelector(selectCanvases) || { owned: [], shared: [] };
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCanvasData, setNewCanvasData] = useState({
    name: '',
    type: 'private'
  });
  const [isCreating, setIsCreating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedCanvas, setSelectedCanvas] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editData, setEditData] = useState({
    id: '',
    name: '',
    type: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [userDetailsMap, setUserDetailsMap] = useState({});
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isDataError, setIsDataError] = useState(null);
  const canvasLoading = useSelector(selectCanvasLoading);
  const { loading: userLoading } = useSelector(selectUserLoading);
  const currentUser = useSelector(selectUser);
  const [showCanvasInviteDialog, setShowCanvasInviteDialog] = useState(false);
  const [canvasInviteData, setCanvasInviteData] = useState({
    email: '',
    role: 'member',
    canvasId: '',
  });
  const [isCanvasInviting, setIsCanvasInviting] = useState(false);
  const user = useSelector(state => state.user.currentUser);
  const members = useSelector(state => state.group.members) || [];
  const { updateSelectedCanvas } = useCanvas();
  const currentPlan = useSelector(selectCurrentPlan);
  const sharedByMe = useSelector(state => state.canvas.sharedByMe) || [];

  let userInvitations;
  // Fetch canvases on mount
  useEffect(() => {
    let isMounted = true;
    let timeoutId;

    const loadInitialData = async () => {
      if (!isMounted) return;
      setIsDataLoading(true);
      setIsDataError(null);

      timeoutId = setTimeout(() => {
        if (isMounted) {
          setIsDataError('Loading timed out. Please refresh the page.');
          setIsDataLoading(false);
        }
      }, 25000);

      try {
        await dispatch(fetchUserData()).unwrap();
        await dispatch(fetchSubscriptionData()).unwrap();

        const results = await Promise.allSettled([
          dispatch(fetchUserCanvases()).unwrap(),
          dispatch(fetchCanvasesSharedByMe()).unwrap(),
          dispatch(fetchGroupData()).unwrap(),
          dispatch(fetchUserInvitations()).unwrap()
        ]);

        const rejected = results.find(result => result.status === 'rejected');
        if (rejected) {
          const errorMessage = rejected.reason?.message || rejected.reason || 'Failed to load canvas data';
          throw new Error(errorMessage);
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        if (isMounted) {
          setIsDataError(error?.message || 'Failed to load canvas data');
        }
      } finally {
        clearTimeout(timeoutId);
        if (isMounted) {
          setIsDataLoading(false);
        }
      }
    };

    loadInitialData();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [dispatch]);

  useEffect(() => {
    const fetchOwnerDetails = async () => {
      if (activeTab === 'Shared Canvas' && Array.isArray(sharedCanvases)) {
        const newMap = {}; // create a fresh map
        for (const canvas of sharedCanvases) {
          const ownerId = canvas.userId;
          if (ownerId && !newMap[ownerId]) {
            try {
              // Assumes fetchUserById returns an object with name and email
              const userData = await dispatch(fetchUserById(ownerId)).unwrap();
              newMap[ownerId] = userData;
            } catch (error) {
              console.error('Failed to fetch details for userId:', ownerId, error);
            }
          }
        }
        setUserDetailsMap(newMap);
      } else {
        setUserDetailsMap({});
      }
    };
    fetchOwnerDetails();
  }, [activeTab, sharedCanvases, dispatch]);

  // Fetch sharedByMe canvases on mount or when activeTab changes to Shared Canvas
  useEffect(() => {
    if (activeTab === 'Shared Canvas') {
      dispatch(fetchCanvasesSharedByMe());
    }
  }, [dispatch, activeTab]);

  // Add data validation check
  const isDataValid = () => {
    return (
      currentUser &&
      Array.isArray(ownedCanvases) &&
      Array.isArray(sharedCanvases) &&
      !canvasLoading &&
      !userLoading
    );
  };

  // Add loading and error states at the start of your render
  if ( canvasLoading || userLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="relative">
          <div className="h-12 w-12 rounded-full border-t-2 border-b-2 border-blue-600 animate-spin"></div>
        </div>
        <p className="mt-4 text-gray-600">Loading canvas data...</p>
      </div>
    );
  }


  if (isDataError) {
  return (
    <DataStatusMessage
      type="error"
      message={isDataError}
      buttonText="Retry Loading"
      onButtonClick={() => window.location.reload()}
    />
    )};

  if (!isDataValid()) {
  return (
    <DataStatusMessage
      type="refresh"
      message="Unable to load canvas data. Please try again."
      buttonText="Refresh Page"
      onButtonClick={() => window.location.reload()}
    />
  )};
  // Handle canvas sharing
  const handleCanvasShare = async (e) => {
  e.preventDefault();

  // Check for self-invitation
  if (canvasInviteData.email.toLowerCase() === user?.email.toLowerCase()) {
    toast.error("You cannot share with yourself");
    return;
  }

  const emailToCheck = canvasInviteData.email.toLowerCase();
  
  // Check in members array (accepted members from subscription)
  const isInMembers = Array.isArray(members) && members.some(m => 
    m.email && typeof m.email === 'string' && 
    m.email.toLowerCase() === emailToCheck
  );
  
  // Check in currentPlan.members (subscription members)
  const isInPlanMembers = Array.isArray(currentPlan?.members) && 
    currentPlan.members.some(m => 
      m.email && typeof m.email === 'string' && 
      m.email.toLowerCase() === emailToCheck
    );

  const isMember = isInMembers || isInPlanMembers;

  if (!isMember) {
    toast.error('You can only share with members of your plan!');
    return;
  }

  setIsCanvasInviting(true);
  try {
    const shareData = {
      ...canvasInviteData,
      email: canvasInviteData.email.toLowerCase().trim()
    };
    await dispatch(shareCanvas(shareData)).unwrap();
    setShowCanvasInviteDialog(false);
    await dispatch(fetchUserCanvases());
    toast.success('Canvas shared successfully!');
    setCanvasInviteData({ email: '', role: 'member', canvasId: selectedCanvas?._id || '' });
  } catch (error) {
    toast.error(error || 'Failed to share canvas');
    setShowCanvasInviteDialog(false)
    console.error('Failed to share canvas:', error);
  } finally {
    setIsCanvasInviting(false);
  }
};
  
   // Handlers remain as before for create, update and delete…
  const handleCreateCanvas = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      await dispatch(createCanvas(newCanvasData)).unwrap();
      toast.success('Canvas created successfully!');
      setShowAddForm(false);
      setNewCanvasData({ name: '', type: 'private' });
      await dispatch(fetchUserCanvases()).unwrap();
    } catch (error) {
      toast.error(error.message || 'Failed to create canvas');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteCanvas = async () => {
    if (!selectedCanvas) return;
    setShowDeleteConfirm(false);
    setIsDeleting(true);
    try {
      await dispatch(deleteCanvas(selectedCanvas._id)).unwrap();
      toast.success('Canvas deleted successfully');
      setSelectedCanvas(null);
      await dispatch(fetchUserCanvases()).unwrap();
    } catch (error) {
      toast.error(error || 'Failed to delete canvas');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditCanvas = async (e) => {
    e.preventDefault();
    setShowEditForm(false);
    setIsEditing(true);
    try {
      await dispatch(updateCanvas(editData)).unwrap();
      toast.success('Canvas updated successfully');
      setEditData({ id: '', name: '', type: '' });
      await dispatch(fetchUserCanvases()).unwrap();
    } catch (error) {
      toast.error(error || 'Failed to update canvas');
    } finally {
      setIsEditing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex border-b">
        <button
          className={`px-4 py-2 text-lg font-medium ${
            activeTab === 'My Canvas'
              ? 'border-b-2 border-btn-primary text-btn-primary'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('My Canvas')}
        >
          My Canvas
        </button>
        <button
          className={`px-4 py-2 text-lg font-medium ${
            activeTab === 'Shared Canvas'
              ? 'border-b-2 border-btn-primary text-btn-primary'
              : 'text-gray-500 hover:text-gray-700'
          } ${ currentPlan?.plan?.planKey === 'free' ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={currentPlan?.plan?.planKey === 'free'}
          onClick={() => {
            if (currentPlan?.plan?.planKey === 'free') {
              toast.info("You can't view shared canvases in the Free plan. Please upgrade your plan.");
              return;
            }
            setActiveTab('Shared Canvas');
          }}
        >
          Shared Canvas
        </button>
      </div>

      {activeTab === 'My Canvas' && (
        <>
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-black">My Canvas</h3>
            {!(currentUser.role ==='kid' && ownedCanvases?.length>=1) && (
              <button
                onClick={() => setShowAddForm(true)}
                className="add-btn-settings"
              >
                Add Canvas
              </button>
            )}
          </div>

          {showAddForm && (
            <form onSubmit={handleCreateCanvas} className="mb-4 p-4 bg-gray-50 rounded-lg w-full">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="w-full">
                  <input
                    type="text"
                    placeholder="Canvas Name"
                    value={newCanvasData.name}
                    onChange={e => setNewCanvasData({ ...newCanvasData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-btn-primary focus:border-transparent"
                    required
                  />
                </div>
                <div className="w-full">
                  <select
                    value={newCanvasData.type}
                    onChange={e => setNewCanvasData({ ...newCanvasData, type: e.target.value })}
                    className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-btn-primary focus:border-transparent"
                  >
                    <option value="private">Private</option>
                    <option value="shared">Shared</option>
                  </select>
                </div>
              </div>
              <div className="mt-4 flex flex-col-reverse sm:flex-row justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="w-full sm:w-auto px-4 py-1.5 border rounded-lg text-sm hover:bg-gray-100 mb-2 sm:mb-0"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className="w-full sm:w-auto add-btn-settings disabled:opacity-75"
                >
                  {isCreating ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Creating...
                    </span>
                  ) : (
                    'Create Canvas'
                  )}
                </button>
              </div>
            </form>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left py-3 px-4">Canvas Name</th>
                  <th className="text-left py-3 px-4">Created by</th>
                  <th className="text-left py-3 px-4">Role</th>
                  <th className="text-left py-3 px-4">Type</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Action</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {ownedCanvases?.map(canvas => (
                  <tr key={canvas._id} className="border-b">
                    <td className="py-3 px-4">{canvas.name}</td>
                    <td className="py-3 px-4">Me</td>
                    <td className="py-3 px-4">Owner</td>
                    <td className="py-3 px-4 capitalize">{canvas.type}</td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">
                        Active
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-4">
                        <button
                                    onClick={() => {
                                      setSelectedCanvas(canvas);
                                      setCanvasInviteData({
                                        email: '',
                                        role: 'member',
                                        canvasId: canvas._id,
                                      });
                                      setShowCanvasInviteDialog(true);
                                    }}
                          className="text-lg px-3 py-1 rounded bg-btn-primary text-white hover:opacity-90"
                        >
                          Share
                        </button>
                        <button
                          onClick={() => {
                            setSelectedCanvas(canvas);
                            setShowDeleteConfirm(true);
                          }}
                          className="text-lg px-3 py-1 rounded text-red-600 border border-red-200 hover:bg-red-50"
                        >
                          <FaTrash />
                        </button>
                        <button
                          onClick={() => {
                            setEditData({
                              id: canvas._id,
                              name: canvas.name,
                              type: canvas.type
                            });
                            setShowEditForm(true);
                          }}
                          className="text-lg px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                        >
                          <FaEdit />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === 'Shared Canvas' && (
        <>
          <h3 className="text-lg font-semibold text-btn-primary  mb-4">Shared Canvas</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left py-3 px-4">Canvas Name</th>
                  <th className="text-left py-3 px-4">Created by</th>
                  <th className="text-left py-3 px-4">Email</th>
                  <th className="text-left py-3 px-4">Role</th>
                  <th className="text-left py-3 px-4">Type</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Action</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {canvasLoading && (
                  <tr>
                    <td colSpan={7} className="py-4 text-center text-gray-500">Loading canvases you shared...</td>
                  </tr>
                )}
                {!canvasLoading && sharedByMe.length > 0 && (
                  <>
                    <tr>
                      <td colSpan={7} className="font-semibold text-gray-700 bg-gray-50">Canvases Shared By You</td>
                    </tr>
                    {sharedByMe.map((canvas, idx) => (
                      <tr key={`shared-by-me-${idx}`} className="border-b">
                        <td className="py-3 px-4">{canvas.canvasName}</td>
                        <td className="py-3 px-4">Me</td>
                        <td className="py-3 px-4">{canvas.email}</td>
                        <td className="py-3 px-4">{canvas.role}</td>
                        <td className="py-3 px-4 capitalize">{canvas.type}</td>
                        <td className="py-3 px-4">{canvas.status}</td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => {
                              // You may want to fetch and set the canvas by name or id if needed
                              toast.info(`Viewing ${canvas.canvasName}`);
                              // Optionally navigate or updateSelectedCanvas if you have the id
                            }}
                            className="add-btn-settings"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </>
                )}
                {sharedCanvases?.map(canvas => (
                  <tr key={canvas._id} className="border-b">
                    <td className="py-3 px-4">{canvas.name}</td>
                    {/* Render owner details from userDetailsMap */}
                    <td className="py-3 px-4">{userDetailsMap[canvas.userId]?.name || 'Unknown'}</td>
                    <td className="py-3 px-4">{userDetailsMap[canvas.userId]?.email || 'Unknown'}</td>
                    <td className="py-3 px-4 capitalize">{canvas?.roles?.find(r => r.userId === currentUser._id)?.role || '-'}</td>
                    <td className="py-3 px-4 capitalize">{canvas.type}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-sm ${
                        canvas.status === 'accepted' ||'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {canvas.status || 'pending'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {canvas.status === 'pending' ? (
                        <div className="flex items-center space-x-2">
                          <button className="text-sm px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700">
                            Accept
                          </button>
                          <button className="text-sm px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700">
                            Decline
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            updateSelectedCanvas(canvas);
                            navigate('/dashboard');
                            toast.success(`View ${canvas.name}`);
                          }}
                          className="add-btn-settings"
                        >
                          View
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Canvas</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{selectedCanvas?.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedCanvas(null);
                }}
                className="px-4 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCanvas}
                disabled={isDeleting}
                className="px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-75"
              >
                {isDeleting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Deleting...
                  </span>
                ) : (
                  'Delete Canvas'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Canvas Form */}
      {showEditForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Edit Canvas</h3>
            <form onSubmit={handleEditCanvas}>
              <div className="space-y-4">
                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-1">
                    Canvas Name
                  </label>
                  <input
                    type="text"
                    value={editData.name}
                    onChange={e => setEditData({ ...editData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    value={editData.type}
                    onChange={e => setEditData({ ...editData, type: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="private">Private</option>
                    <option value="shared">Shared</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditForm(false);
                    setEditData({ id: '', name: '', type: '' });
                  }}
                  className="px-4 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isEditing}
                  className="add-btn-settings"
                >
                  {isEditing ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Updating...
                    </span>
                  ) : (
                    'Update Canvas'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Canvas Invite Dialog */}
      {showCanvasInviteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">  Share Canvas</h3>
            <form onSubmit={handleCanvasShare}>
              <div className="space-y-4">
                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={canvasInviteData.email}
                    onChange={e => setCanvasInviteData({ ...canvasInviteData, email: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={canvasInviteData.role}
                    onChange={e => setCanvasInviteData({ ...canvasInviteData, role: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="member">Member</option>
                    <option value="manager">Manager</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCanvasInviteDialog(false)}
                  className="px-4 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCanvasInviting}
                  className="add-btn-settings"
                >
                  {isCanvasInviting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Sharing...
                    </span>
                  ) : (
                    'Share'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCanvasContent;